import * as React from 'react'
import {IntlShape, useIntl} from 'react-intl'
import {css} from '@emotion/react'
import styled from '@emotion/styled'

import {ScoreRatingCompact} from '@daedalus/atlas/experimental/ScoreRatingCompact'
import {ScoreRating, ScoreRatingLoader} from '@daedalus/atlas/ScoreRating'
import {
  getReviewSentimentKey,
  reviewSentimentMessages
} from '@daedalus/core/src/hotel/reviewSentiments'
import {toOneDecimalOrInteger} from '@daedalus/core/src/utils/number'

export {ScoreRatingLoader as ReviewRatingLoader}

interface Props {
  rating: number
  totalRatingCount?: number
  withIcon?: boolean
  dataId?: string
  underlineSubLabel?: boolean
  isCompact?: boolean
  hotelId?: string
}

const DEFAULT_DATA_ID = 'ReviewRating'

const LabelWrapper = styled('span')<{underline: boolean}>(
  ({underline}) => css`
    ${underline && 'text-decoration: underline'}
  `
)

const getSubLabel = (
  totalRatingCount: Props['totalRatingCount'],
  isCompact: Props['isCompact'],
  formatMessage: IntlShape['formatMessage']
) => {
  if (!totalRatingCount) return null

  return isCompact
    ? totalRatingCount?.toLocaleString()
    : formatMessage(
        {id: 'reviewsCount', defaultMessage: '{reviewCount} reviews'},
        {reviewCount: totalRatingCount.toLocaleString()}
      )
}

export const ReviewRating = ({
  rating,
  totalRatingCount,
  underlineSubLabel = false,
  dataId = DEFAULT_DATA_ID,
  isCompact = false,
  hotelId
}: Props) => {
  const {formatMessage} = useIntl()

  const sentimentMessageKey = getReviewSentimentKey(rating)
  const translatedSentiment = sentimentMessageKey
    ? reviewSentimentMessages[sentimentMessageKey]
    : null

  const formattedRating = toOneDecimalOrInteger(rating)

  const ScoreRatingComponent = isCompact ? ScoreRatingCompact : ScoreRating

  if (formattedRating === null) {
    return null
  }

  const subLabel = getSubLabel(totalRatingCount, isCompact, formatMessage)

  return (
    <div data-id={dataId}>
      <ScoreRatingComponent
        value={formattedRating}
        label={translatedSentiment && formatMessage(translatedSentiment)}
        subLabel={
          <LabelWrapper underline={underlineSubLabel}>{subLabel}</LabelWrapper>
        }
        isCompact={isCompact}
        scoreId={hotelId && `HotelCardMobileScoreValue-${hotelId}`}
        scoreLabelId={hotelId && `HotelCardMobileScoreLabel-${hotelId}`}
        scoreSubLabelId={hotelId && `HotelCardMobileScoreSubLabel-${hotelId}`}
      />
    </div>
  )
}
